import React, { Suspense, useState } from 'react';
import { MemoryRouter } from 'react-router';
import { ThemeProvider } from '@emotion/react';
import { useMount } from 'react-use';
import { observer } from 'mobx-react';
import 'dayjs/locale/de';
import {
  ExternalSearchParams,
  ExternalSearchParamsContextProps,
  MEDIAQUERY_DEFAULTS,
  Overlay,
  useMediaQuery
} from '@ibe/components';
import theme from '../Theme/theme';
import { boot } from '../Config/config';
import initI18next from '../Translations/setup';
import Checkout from '../Pages/Checkout/Checkout';
import { ThgConfigModel } from '../Config/ThgConfigModel';
import { ConfigModel } from '@ibe/services';
import RouteBroadcast from '../RouteBroadcast';
import '../Theme/Styles/widgets.scss';
import { BookingErrorProvider } from '../Util/useBookingError';
import { stepSessionStorage } from '../Util/globals';
import { extendedConfigContext } from '../Config/extendedConfigContext';

const CheckoutWidget = observer(
  (
    props: ExternalSearchParamsContextProps & {
      rerouteUrl: string;
      homepageUrl?: string;
      thgConfig: ThgConfigModel;
      configOverrides?: ConfigModel;
    }
  ) => {
    const {
      widgetSessionKeyPrefix: keyPrefix,
      rerouteUrl,
      homepageUrl,
      thgConfig,
      configOverrides,
      mode
    } = props;
    const [bootstrappingFinished, setBootstrappingFinished] = useState<boolean>(false);
    const [hasBookingError, setHasBookingError] = useState<boolean>(false);
    const matches = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.xl });

    const widgetSessionKeyPrefix = keyPrefix || document.body.id || '';

    useMount(async () => {
      const currentStep = stepSessionStorage.get();
      const isConfirmationPage = currentStep === 'confirmation';
      await initI18next(
        thgConfig.baseUrl,
        thgConfig.version,
        configOverrides?.tenant ?? undefined,
        mode
      );
      const bookingError = await boot(
        false,
        widgetSessionKeyPrefix,
        configOverrides,
        isConfirmationPage,
        thgConfig
      );
      if (bookingError) {
        setHasBookingError(true);
      }
      setBootstrappingFinished(true);
      const event = new CustomEvent('isocheckoutwidgetready');
      window.dispatchEvent(event);
    });

    return bootstrappingFinished ? (
      <ThemeProvider theme={theme}>
        <div id="iso" className="iso__transparent">
          <Suspense fallback={<Overlay fullOpacity />}>
            <extendedConfigContext.Provider value={thgConfig}>
              <MemoryRouter>
                <BookingErrorProvider hasBookingError={hasBookingError}>
                  <ExternalSearchParams
                    {...props}
                    widgetSessionKeyPrefix={widgetSessionKeyPrefix}
                    goToDetails
                  >
                    <Checkout
                      widgetType="CHECKOUT"
                      rerouteUrl={rerouteUrl}
                      homepageUrl={homepageUrl}
                      navBarOffset={matches ? 133 : 91}
                    />
                    <RouteBroadcast />
                  </ExternalSearchParams>
                </BookingErrorProvider>
              </MemoryRouter>
            </extendedConfigContext.Provider>
          </Suspense>
        </div>
      </ThemeProvider>
    ) : (
      <div id="iso" style={{ minHeight: '100vh', background: 'none' }}></div>
    );
  }
);

export default CheckoutWidget;
